import { ChangeDetectionStrategy, Component, effect, inject, model, signal, WritableSignal } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  MbsSize,
  ModalService,
  TableCellDirective,
  TableGridComponent,
  TableHeader,
  TextEllipsisComponent,
  TextEllipsisPrependDirective
} from 'mbs-ui-kit';
import { Company, PermissionsEnum } from 'mbs-ui-shared@models';
import { AdministratorService } from 'mbs-ui-shared@services/administrator.service';
import { noop } from 'rxjs';
import { CompanyWizardFacade } from 'wizards/services/entity-wizards/company/company-wizard.facade';
import { CreateAdministratorModalComponent } from '../../modals/create-administrator-modal/create-administrator-modal.component';
import { CompanyAdministratorsStepDefaultValue, CompanyAdministratorsStepValue } from '../../models/company-wizard-models';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, I18NextPipe, TableCellDirective, TableGridComponent, TextEllipsisComponent, TextEllipsisPrependDirective],
  selector: 'mbs-company-administrators-step',
  standalone: true,
  templateUrl: './company-administrators-step.component.html'
})
export class CompanyAdministratorsStepComponent {
  private readonly administratorService = inject(AdministratorService);
  private readonly companyWizardFacade = inject(CompanyWizardFacade);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly modalService = inject(ModalService);

  public readonly value = model<CompanyAdministratorsStepValue>(CompanyAdministratorsStepDefaultValue);

  public readonly loading: WritableSignal<boolean> = signal(false);
  public readonly elementSelectors = {
    id: { createButton: 'company-create-administrator-button', table: 'company-administrators-table' }
  };

  public readonly headers: TableHeader[] = [
    { name: this.i18nPipe.transform('shared:tableHeaders.firstName'), overflow: true, gridColSize: '1fr' },
    { name: this.i18nPipe.transform('shared:tableHeaders.lastName'), overflow: true, gridColSize: '1fr' },
    { name: this.i18nPipe.transform('shared:tableHeaders.email'), overflow: true, gridColSize: '2fr' },
    { name: this.i18nPipe.transform('shared:tableHeaders.company'), overflow: true, gridColSize: '1fr' }
  ];

  constructor() {
    this.initialSubscribes();
  }

  public createAdministratorClickHandler(): void {
    this.modalService
      .openCustom(CreateAdministratorModalComponent, { size: MbsSize.sm })
      .then(() => this.loadAdministrators(this.companyWizardFacade.company()))
      .catch(noop);
  }

  // Private section
  private initialSubscribes(): void {
    effect(() => this.loadAdministrators(this.companyWizardFacade.company()));
  }

  private loadAdministrators(company: Company): void {
    if (!company) return;

    this.loading.set(true);

    this.administratorService.get({ companyId: company.id, includeAllCompanies: true }).subscribe({
      next: (result) => {
        this.value.update((value) => ({
          ...value,
          administrators: result.data.map((adm) => ({
            ...adm,
            company:
              adm.companies?.map((c) => c.name).join(', ') ||
              (adm.permissions?.some((p) => p === PermissionsEnum.AccessToAllCompanies)
                ? this.i18nPipe.transform('shared:allCompanies')
                : '-')
          }))
        }));
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }
}
